<script setup>
import { nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

import { DEV_MODE } from '@/config/dev-mode';
import { CONTENTS, CONTENTS_PAGE_MAP } from './constants';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi.js';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { addContentLogAPI } from '@/api/logServiceApi';

import GuidePersonality01 from './GuidePersonality01.vue';
import GuidePersonality02 from './GuidePersonality02.vue';
import personalitySub01 from './ContentsPersonality01.vue';
import personalitySub02 from './ContentsPersonality02.vue';
import personalitySub03 from './ContentsPersonality03.vue';
import personalitySub04 from './ContentsPersonality04.vue';
import personalitySub05 from './ContentsPersonality05.vue';
import personalitySub06 from './ContentsPersonality06.vue';
import personalitySub07 from './ContentsPersonality07.vue';
import personalitySub08 from './ContentsPersonality08.vue';
import personalitySub09 from './ContentsPersonality09.vue';
import personalitySub10 from './ContentsPersonality10.vue';
import personalitySub11 from './ContentsPersonality11.vue';
import personalitySub12 from './ContentsPersonality12.vue';
import personalitySub13 from './ContentsPersonality13.vue';
import personalitySub14 from './ContentsPersonality14.vue';
import personalitySub15 from './ContentsPersonality15.vue';
import personalitySub16 from './ContentsPersonality16.vue';
import personalitySub17 from './ContentsPersonality17.vue';
import personalitySub18 from './ContentsPersonality18.vue';
import personalitySub19 from './ContentsPersonality19.vue';
import personalitySub20 from './ContentsPersonality20.vue';

import ContentsList from '@/components/saju-insight/common/ContentsList.vue';
import { isObjectEmpty } from '@/composables/functions';

const props = defineProps({
  updateSelectedDescNav: {
    type: Function,
  },
  hasPermission: {
    type: Boolean,
  },
});
const emit = defineEmits(['goPopAnalysis']);

const route = useRoute();

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();

const { sajuData } = storeToRefs(dataStore);

// =============== 페이지 ===============
/** 페이지 이동 (로그 없이) */
const moveToPage = (id) => {
  if (id < 3) {
    props.updateSelectedDescNav('');
  } else {
    if (id > 3 && !props.hasPermission) {
      emit('goPopAnalysis');
      return;
    }
    props.updateSelectedDescNav('personalityJob');
  }
  indexNo.value = id;
};

/** index값으로 페이지 이동 (목차 클릭 또는 페이지네이션) */
const updateIndexNo = (id, tocIdx) => {
  if (tocIdx != null) {
    // 목차 리스트 클릭
    addContentLogAPI({
      contentType: props.hasPermission ? 'ap_personality_toc_paid_opt3' : 'ap_personality_toc_free_opt3',
      option3: String(tocIdx),
    });
  } else {
    // 이전/다음 화살표 클릭
    addContentLogAPI({
      contentType: 'ap_personality_page_opt3',
      option3: String(id),
    });
  }
  moveToPage(id);
};

// 인덱스
const indexNo = ref(1);
watch(indexNo, (newIndex) => {
  store.setPersonalityIndex(newIndex);
});
onUnmounted(() => {
  store.setPersonalityIndex(1);
});

watch(
  () => store.selectedContentIndex,
  (newIdx) => {
    const target = CONTENTS_PAGE_MAP[newIdx];
    if (target) moveToPage(target);
  },
  { immediate: true },
);

/** 표지로 이동 */
const goToOverView = () => {
  store.clickMainMenu('/overviewTendency');
};

// =============== 성향분석 데이터 및 상태 ===============
const guestId = route.params.id;

const isFetchingPersonality = ref(true);

const personalityParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestId,
    lang: 'KOR',
    birth: sajuData.value.solar ?? null,
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 3,
  };
});

const isReadyToShow = computed(() => {
  return (
    !isFetchingPersonality.value &&
    (!isObjectEmpty(dataStore.pData) || !isObjectEmpty(dataStore.pSubData))
  );
});

// 진로 추천 이지 개수 인 (3상면 true, 미만면 false)
const showFullLayout = computed(() => {
  if (!dataStore.pData?.진로과?.반추천분야) return true;
  const majorRecoListAll = Object.entries(dataStore.pData.진로과.반추천분야);
  return majorRecoListAll.length >= 3;
});

/** 성향분석 데이터 초기화 */
const initPersonalityData = async () => {
  if (!props.hasPermission) {
    await fetchSubData();
  } else {
    await fetchPersonalityData();
  }
};

/** 성향분석 무료 이청 */
const fetchSubData = async () => {
  isFetchingPersonality.value = true;
  try {
    if (isObjectEmpty(dataStore.pSubData)) {
      const subDataParams = {
        ...personalityParams.value,
        analysisTypeId: 8,
      };

      const result = await fetchBaseServiceAPI(subDataParams);

      dataStore.setPSubData(result?.data?.성향종합분석);
    }
  } catch (e) {
  } finally {
    isFetchingPersonality.value = false;
  }
};

/** 성향분석 이청 */
const fetchPersonalityData = async () => {
  isFetchingPersonality.value = true;
  try {
    if (isObjectEmpty(dataStore.pData)) {
      const result = await fetchBaseServiceAPI(personalityParams.value);

      dataStore.setPData(result?.data);
      store.setAnalysisResult(result?.data);
    }
  } catch (error) {
    DEV_MODE && console.error('/personality.vue fetchPersonalityData 실패:', error);
  } finally {
    isFetchingPersonality.value = false;
  }
};

watch(
  sajuData,
  async (newData, oldData) => {
    if (!isObjectEmpty(newData)) {
      await initPersonalityData();
    }
  },
  { immediate: true },
);

watch(
  () => props.hasPermission,
  async (newValue, oldValue) => {
    if (!isObjectEmpty(sajuData.value) && !oldValue && newValue) {
      await initPersonalityData();
    }
  },
);

// =============== 평가하기 데이터 ===============
const ratingResult = ref(0);
const ratingStatus = ref(false);

/** 평가조회 */
const getSajuPersonalityRate = async () => {
  if (!props.hasPermission) return;

  try {
    const params = {
      serviceName: 'PERSONALITY_ANALYSIS',
      objectKey: `${guestId}`,
    };

    const result = await getDailyFortuneRateAPI(params);

    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

/** 평가하기 */
const postSajuPersonalityRate = async (score) => {
  try {
    const params = {
      score,
      serviceName: 'PERSONALITY_ANALYSIS',
      objectKey: `${guestId}`,
    };

    const result = await postDailyFortuneRateAPI(params);

    if (result) {
      ratingStatus.value = true;
      getSajuPersonalityRate();
    }
  } catch (error) {}
};

onMounted(() => {
  getSajuPersonalityRate();
});
</script>

<template>
  <div v-if="store.selectedMenu.endsWith('personality')" class="full-height">
    <div v-if="isFetchingPersonality" class="analysis__loading-overlay full-height">
      <div class="loading-olverlay__img"></div>
      성향 분석중입니다!
    </div>
    <GuidePersonality01 v-if="indexNo === 1" class="full-height" />
    <GuidePersonality02 v-if="indexNo === 2" class="full-height" />
    <personalitySub01
      v-if="indexNo === 3 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
      :pSubData="dataStore.pSubData"
      :userName="store.userName"
    />
    <personalitySub02
      v-if="indexNo === 4 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub17
      v-if="indexNo === 5 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
      :userName="store.userName"
    />
    <personalitySub03
      v-if="indexNo === 6 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub04
      v-if="indexNo === 7 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub05
      v-if="indexNo === 8 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub06
      v-if="indexNo === 9 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub07
      v-if="indexNo === 10 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub08
      v-if="indexNo === 11 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub09
      v-if="indexNo === 12 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub10
      v-if="indexNo === 13 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub11
      v-if="indexNo === 14 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub12
      v-if="indexNo === 15 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub13
      v-if="indexNo === 16 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
    />
    <personalitySub14
      v-if="indexNo === 17 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
      :userName="store.userName"
    />
    <personalitySub15
      v-if="indexNo === 18 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
      :userName="store.userName"
    />
    <personalitySub16
      v-if="indexNo === 19 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
      :userName="store.userName"
    />

    <personalitySub18
      v-if="indexNo === 20 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
      :userName="store.userName"
    />
    <personalitySub19
      v-if="indexNo === 21 && isReadyToShow"
      class="full-height"
      :pData="dataStore.pData"
      :userName="store.userName"
    />
    <personalitySub20
      v-if="indexNo === 22 && isReadyToShow"
      class="full-height"
      :ratingResult="ratingResult"
      :ratingStatus="ratingStatus"
      :showFullLayout="showFullLayout"
      @goToOverView="goToOverView"
      @postRate="postSajuPersonalityRate"
    />
  </div>

  <div class="contentsListBtnBox">
    <ContentsList
      :items="CONTENTS"
      :curPage="indexNo"
      :hasPermission="hasPermission"
      @updateIndexNo="updateIndexNo"
      @goToOverView="goToOverView"
    />
  </div>
</template>
<style>
@import '/src/assets/nangman_style/personality.scss';
</style>
